import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { ApiService } from '../../_core/_services/api.service';
import { DepositDto } from '../../_core/_model/deposits';
import { ApiMeta } from '../../_core/_model/api-response';
import { FormatService } from '@shared/_core/_services/format';

@Component({
  selector: 'app-deposit-history',
  standalone: false,
  templateUrl: './deposit-history.html',
  styleUrl: './deposit-history.css'
})
export class DepositHistory implements OnInit, OnDestroy {
  loading = false;
  page = 1;
  deposits: DepositDto[] = [];
  meta: ApiMeta | null = null;
  highlightedRef = '';
  pollingSub?: Subscription;
  filterWalletId: string | null = null;

  selectedDeposit: DepositDto | null = null;
  showDetailsModal = false;

  url: any;

  constructor(
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private format: FormatService,
  ) {
    this.url = this.router.url.slice(1);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.highlightedRef = params['ref'] || '';
      this.filterWalletId = params['walletId'] || null;
      this.loadDeposits();
    });

    this.startPolling();
  }

  backToWallet(): void {
    if (!this.filterWalletId) return;
    this.router.navigate(['/wallets/manage', this.filterWalletId]);
  }
  ngOnDestroy(): void {
    this.pollingSub?.unsubscribe();
  }

  loadDeposits(page:number = 1, silent = false): void {
    this.page = page;
    if (!silent) this.loading = true;

    const request$ = this.filterWalletId
      ? this.api.getWalletDeposits(this.filterWalletId, this.page)
      : this.api.getUserDeposits(this.page);

    request$.subscribe({
      next: (res) => {
        this.deposits = res.data || [];
        this.meta = res.meta || null;
      },
      error: () => {
        if (!silent) this.deposits = [];
      },
      complete: () => {
        if (!silent) this.loading = false;
      }
    });
  }

  clearFilter(): void {
    this.filterWalletId = null;
    this.router.navigate(['/wallets/deposit/history']);
  }

  startPolling(): void {
    this.pollingSub = interval(10000).subscribe(() => {
      const hasPending = this.deposits.some(x =>
        ['pending', 'processing', 'initiated', 'awaitingdocument', 'documentreview'].includes((x?.status || '').toLowerCase())
      );

      if (hasPending) {
        this.loadDeposits(this.page, true);
      }
    });
  }

  badgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'confirmed':
      case 'successful':
        return 'bg-success-subtle text-success';
      case 'failed':
      case 'cancelled':
        return 'bg-danger-subtle text-danger';
      case 'processing':
      case 'pending':
      case 'initiated':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  isHighlighted(item: DepositDto): boolean {
    return !!this.highlightedRef && item.externalReference === this.highlightedRef;
  }

  openDetails(item: DepositDto): void {
    this.selectedDeposit = item;
    this.showDetailsModal = true;
  }

  closeDetails(): void {
    this.showDetailsModal = false;
    this.selectedDeposit = null;
  }


  requiresDocument(item: DepositDto): boolean {
    return ['awaitingdocument', 'documentreview'].includes((item.status || '').toLowerCase());
  }

  openDocumentRequests(): void {
    this.router.navigate(['/account/additional-documents']);
  }

  canConfirm(item: DepositDto): boolean {
    return (item.status || '').toLowerCase() === 'pending';
  }

  getDepositAndWithdrawalStatusClass(status: string, isWithdrawal: boolean): string {
    return this.format.getDepositAndWithdrawalStatusClass(status, isWithdrawal);
  }

  confirm(item: DepositDto): void {
    if (!item.depositId) return;
    this.router.navigateByUrl(`/wallets/deposit/confirmation/${item.depositId}`);
  }
}